/**
 * Worker timesheet API — read + entry CRUD.
 *
 * `getFieldTimesheet` принимает optional date (YYYY-MM-DD); backend возвращает
 * `FieldTimesheetResponse` с уже сгруппированными `my_work_types`, `entries`,
 * `my_recent_projects` и метаданными окна backfill.
 *
 * `createFieldEntry` / `replaceFieldEntry` — append-only flow (см. §2.3).
 * PUT возвращает новую entry с новым id (старая soft-deleted на бэке).
 * `searchProjects` — typeahead для ProjectPicker (debounced на UI стороне).
 */
import { api } from "./client";
import type { components } from "./types";

export type FieldTimesheetResponse = components["schemas"]["FieldTimesheetResponse"];
export type TimesheetEntry = components["schemas"]["TimesheetEntry"];
export type WorkType = components["schemas"]["WorkType"];
export type Project = components["schemas"]["Project"];
export type ProjectSearchHit = components["schemas"]["ProjectSearchHit"];
export type TimesheetEntryPayload = components["schemas"]["TimesheetEntryPayload"];
export type FieldSignPayload = components["schemas"]["FieldSignPayload"];
export type SignResponse = components["schemas"]["SignResponse"];
export type AssemblyElement = components["schemas"]["AssemblyElement"];
export type PairWorkStatus = components["schemas"]["PairWorkStatus"];

export async function getFieldTimesheet(date?: string): Promise<FieldTimesheetResponse> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : "";
  return api.get<FieldTimesheetResponse>(`/field/timesheet${qs}`);
}

export async function deleteFieldEntry(entryId: number, date?: string): Promise<void> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : "";
  await api.delete<void>(`/field/timesheet/entries/${entryId}${qs}`);
}

export async function createFieldEntry(
  payload: TimesheetEntryPayload,
  date?: string,
): Promise<TimesheetEntry> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : "";
  return api.post<TimesheetEntry>(`/field/timesheet/entries${qs}`, payload);
}

export async function replaceFieldEntry(
  oldEntryId: number,
  payload: TimesheetEntryPayload,
  date?: string,
): Promise<TimesheetEntry> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : "";
  return api.put<TimesheetEntry>(`/field/timesheet/entries/${oldEntryId}${qs}`, payload);
}

export async function signFieldTimesheet(
  payload: FieldSignPayload,
  date?: string,
): Promise<SignResponse> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : "";
  return api.post<SignResponse>(`/field/timesheet/sign${qs}`, payload);
}

export async function searchProjects(q: string): Promise<ProjectSearchHit[]> {
  const qs = q ? `?q=${encodeURIComponent(q)}` : "";
  return api.get<ProjectSearchHit[]>(`/field/projects/search${qs}`);
}

/**
 * Адресный список элементов сборки проекта — для шага «элемент сборки» единого
 * редактора записи (задача 89 §4.2). Резолвится лениво на выбор проекта.
 * Пустой список = проект без типа оборудования / без выбранных элементов —
 * шаг деградирует до пропуска (элемент необязателен).
 */
export async function getProjectAssemblyElements(projectId: number): Promise<AssemblyElement[]> {
  return api.get<AssemblyElement[]>(`/field/projects/${projectId}/assembly-elements`);
}

/**
 * Статус пары (проект, вид работ) для редактора записи (задача 115 §2.1).
 * `completed=true` + дата закрытия → внесённая на эту пару запись будет
 * доработкой; редактор показывает баннер и поле идентификатора. Гейт эндпоинта —
 * `CurrentUser` (нечувствительные данные, доступен self и admin-композеру).
 */
export async function getPairWorkStatus(
  projectId: number,
  workTypeId: number,
): Promise<PairWorkStatus> {
  return api.get<PairWorkStatus>(
    `/field/work-status?project_id=${projectId}&work_type_id=${workTypeId}`,
  );
}
